/**
 * DEV-7091b — Server-Sent-Events parsing for the AI Assistant stream.
 *
 * Faithful port of the Vue 2 `parseSSEStream` + tool-use extraction. The backend
 * proxies the Bedrock agent as `text/event-stream`; each `data:` line is a JSON
 * object carrying one of `{ token }`, `{ done }`, or `{ approval_required }`.
 * Tool invocations arrive inline inside a token as a markdown marker and are
 * split out before the remaining content is appended.
 */
import type { ApprovalData, SseCallbacks } from '../types/ai-assistant.types'

// Matches: **🔧 Using tool: <name> - args: (<args>)**
const TOOL_USE_REGEX = /\*\*🔧\s*Using tool:\s*(.+?)\s*-\s*args:\s*\((.+?)\)\*\*/

export function extractToolUse(token: string): { name: string; args: string } | null {
  const match = TOOL_USE_REGEX.exec(token)
  if (match) return { name: match[1].trim(), args: match[2].trim() }
  return null
}

export function stripToolUseMarkup(token: string): string {
  // Remove full tool-use lines including surrounding newlines.
  return token.replace(/\n*\*\*🔧\s*Using tool:[^*]*\*\*\n*/g, '')
}

/** A minimal reader matching `ReadableStreamDefaultReader<Uint8Array>`. */
export interface StreamReader {
  read(): Promise<{ done: boolean; value?: Uint8Array }>
}

/**
 * Read an SSE stream to completion, dispatching parsed events to `callbacks`.
 * Returns a promise that resolves when the stream ends.
 */
export async function parseSseStream(reader: StreamReader, callbacks: SseCallbacks): Promise<void> {
  const decoder = new TextDecoder()
  let buffer = ''

  function processBuffer(): void {
    const parts = buffer.split('\n\n')
    buffer = parts.pop() ?? ''

    for (const part of parts) {
      for (const line of part.split('\n')) {
        if (line.indexOf('data:') !== 0) continue
        const raw = line.slice(5).trim()
        if (!raw) continue

        let msg: {
          done?: boolean
          approval_required?: ApprovalData
          token?: string
        }
        try {
          msg = JSON.parse(raw)
        } catch {
          continue
        }

        if (msg.done) {
          callbacks.onDone?.()
          continue
        }
        if (msg.approval_required) {
          callbacks.onApprovalRequired?.(msg.approval_required)
          continue
        }
        if (msg.token) {
          const toolInfo = extractToolUse(msg.token)
          if (toolInfo) {
            callbacks.onToolUse?.(toolInfo)
            const remaining = stripToolUseMarkup(msg.token)
            if (remaining) callbacks.onToken?.(remaining)
          } else {
            callbacks.onToken?.(msg.token)
          }
        }
      }
    }
  }

  for (;;) {
    const result = await reader.read()
    if (result.done) {
      if (buffer.trim()) {
        buffer += '\n\n'
        processBuffer()
      }
      callbacks.onDone?.()
      return
    }
    if (result.value) buffer += decoder.decode(result.value, { stream: true })
    processBuffer()
  }
}
